// Sugalarm — 16:9 pan/zoom background cropper (cycle 87).
//
// The kiosk + web app are 16:9 landscape surfaces, so every uploaded
// background must end up ~16:9. This presentational component takes a
// source image (a `File`/`Blob` that is ALREADY a browser-decodable
// format — HEIC is converted to JPEG by the caller before it reaches
// here, see SettingsScreen / the cycle-87 upload flow) and lets the user
// pan + pinch/scroll-zoom it inside a fixed 16:9 frame. The frame IS the
// crop viewport: what you see is what gets exported. The image can never
// zoom out below "cover", so the frame is always fully filled (no
// letterbox / no raw non-landscape passthrough).
//
// On confirm it renders the visible region to a `<canvas>` and exports a
// JPEG Blob (quality ~0.9, long edge capped ~1920 so a phone photo
// doesn't blow the byte cap; never upscaled) and reports the blob's
// pixel dimensions. Vanilla canvas + pointer/wheel handlers — no crop
// library. Works with touch (phone) and mouse (web).

import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "./ui/Button";
import styles from "./BackgroundCropper.module.css";

interface Props {
  /** Source image — must be a browser-decodable format (HEIC already
   *  converted upstream). */
  source: Blob;
  /** Disables Confirm while the export/upload is in flight. */
  busy?: boolean;
  onConfirm: (blob: Blob, width: number, height: number) => void;
  onCancel: () => void;
  /** Cycle 105: crop frame aspect ratio (width / height). Default 16:9
   *  (background); pass `1` for a square avatar crop. */
  aspect?: number;
  /** Long-edge cap (px) of the exported JPEG, never upscaled. Default
   *  1920 (backgrounds); avatars pass 512. */
  outputMaxLongEdge?: number;
  /** Optional dialog copy overrides (default = the cycle-87 16:9 copy). */
  title?: string;
  hint?: string;
}

const MAX_ZOOM = 8; // multiples of the cover scale

export function BackgroundCropper({
  source,
  busy = false,
  onConfirm,
  onCancel,
  aspect = 16 / 9,
  outputMaxLongEdge = 1920,
  title = "Crop to 16:9",
  hint = "Drag to reposition · scroll or pinch to zoom. The framed area fills the display.",
}: Props) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [srcUrl, setSrcUrl] = useState<string | null>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [frameW, setFrameW] = useState(0);
  // Transform: image top-left at (ox, oy) within the frame, scaled by s.
  const [view, setView] = useState({ s: 1, ox: 0, oy: 0 });
  const [exporting, setExporting] = useState(false);

  // ── source → object URL → natural dims ──────────────────────────────
  useEffect(() => {
    const url = URL.createObjectURL(source);
    setSrcUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [source]);

  // ── measure the frame width (responsive) ────────────────────────────
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => setFrameW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [srcUrl]);

  const frameH = frameW / aspect;

  const coverScale = useCallback(() => {
    if (!nat || frameW === 0) return 1;
    return Math.max(frameW / nat.w, frameH / nat.h);
  }, [nat, frameW, frameH]);

  // Clamp the offset so the image always covers the frame at scale `s`.
  const clampOffset = useCallback(
    (s: number, ox: number, oy: number) => {
      if (!nat) return { ox, oy };
      const iw = nat.w * s;
      const ih = nat.h * s;
      const cx = Math.min(0, Math.max(frameW - iw, ox));
      const cy = Math.min(0, Math.max(frameH - ih, oy));
      return { ox: cx, oy: cy };
    },
    [nat, frameW, frameH],
  );

  // ── initialise transform once the image + frame are known ───────────
  useEffect(() => {
    if (!nat || frameW === 0) return;
    const s = coverScale();
    // Centre the image in the frame.
    const ox = (frameW - nat.w * s) / 2;
    const oy = (frameH - nat.h * s) / 2;
    const c = clampOffset(s, ox, oy);
    setView({ s, ox: c.ox, oy: c.oy });
  }, [nat, frameW, frameH, coverScale, clampOffset]);

  // ── pan (single pointer) + pinch (two pointers) ─────────────────────
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gesture = useRef<{
    startView: { s: number; ox: number; oy: number };
    startDist: number;
    startMid: { x: number; y: number };
  } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current = null;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId) || !nat) return;
    const prev = pointers.current.get(e.pointerId)!;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];

    if (pts.length === 1) {
      // Pan — follow the finger/mouse.
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setView((v) => {
        const c = clampOffset(v.s, v.ox + dx, v.oy + dy);
        return { s: v.s, ox: c.ox, oy: c.oy };
      });
      return;
    }
    if (pts.length >= 2) {
      // Pinch — scale around the midpoint of the two pointers.
      const a = pts[0];
      const b = pts[1];
      if (!a || !b) return;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const rect = frameRef.current?.getBoundingClientRect();
      const midX = (a.x + b.x) / 2 - (rect?.left ?? 0);
      const midY = (a.y + b.y) / 2 - (rect?.top ?? 0);
      if (!gesture.current) {
        gesture.current = {
          startView: view,
          startDist: dist || 1,
          startMid: { x: midX, y: midY },
        };
        return;
      }
      const g = gesture.current;
      const minS = coverScale();
      const next = Math.min(
        minS * MAX_ZOOM,
        Math.max(minS, g.startView.s * (dist / g.startDist)),
      );
      // Keep the gesture midpoint anchored to the same image point.
      const k = next / g.startView.s;
      const ox = midX - (g.startMid.x - g.startView.ox) * k;
      const oy = midY - (g.startMid.y - g.startView.oy) * k;
      const c = clampOffset(next, ox, oy);
      setView({ s: next, ox: c.ox, oy: c.oy });
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    if (!nat) return;
    const rect = frameRef.current?.getBoundingClientRect();
    const px = e.clientX - (rect?.left ?? 0);
    const py = e.clientY - (rect?.top ?? 0);
    setView((v) => {
      const minS = coverScale();
      const factor = Math.exp(-e.deltaY * 0.0015);
      const next = Math.min(minS * MAX_ZOOM, Math.max(minS, v.s * factor));
      const k = next / v.s;
      const ox = px - (px - v.ox) * k;
      const oy = py - (py - v.oy) * k;
      const c = clampOffset(next, ox, oy);
      return { s: next, ox: c.ox, oy: c.oy };
    });
  };

  // ── confirm → canvas export ─────────────────────────────────────────
  const confirm = async () => {
    const img = imgRef.current;
    if (!img || !nat || frameW === 0) return;
    setExporting(true);
    try {
      // Source rect (in natural image px) currently shown in the frame.
      const sx = -view.ox / view.s;
      const sy = -view.oy / view.s;
      const sw = frameW / view.s;
      const sh = frameH / view.s;
      // Output: cap the long (16:9 → width) edge, never upscale.
      const outW = Math.round(Math.min(sw, outputMaxLongEdge));
      const outH = Math.round(outW / aspect);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        setExporting(false);
        return;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
      canvas.toBlob(
        (blob) => {
          setExporting(false);
          if (blob) onConfirm(blob, outW, outH);
        },
        "image/jpeg",
        0.9,
      );
    } catch {
      setExporting(false);
    }
  };

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label="Crop image">
      <div className={styles.panel}>
        <div className={styles.title}>{title}</div>
        <div className={styles.hint}>{hint}</div>
        <div
          ref={frameRef}
          className={styles.frame}
          style={{ height: frameH || undefined }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
        >
          {srcUrl ? (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <img
              ref={imgRef}
              src={srcUrl}
              alt="Background to crop"
              draggable={false}
              onLoad={(e) =>
                setNat({
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight,
                })
              }
              className={styles.img}
              style={{
                transform: `translate(${view.ox}px, ${view.oy}px) scale(${view.s})`,
              }}
            />
          ) : null}
          {/* 16:9 framing guide overlay (non-interactive). */}
          <div className={styles.grid} aria-hidden />
        </div>
        <div className={styles.actions}>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy || exporting}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={confirm}
            disabled={busy || exporting || !nat}
          >
            {busy || exporting ? "Working…" : "Use image"}
          </Button>
        </div>
      </div>
    </div>
  );
}
